import type { CostLine, KoppelTijdlijnItem } from '@fleet/shared';
import { catToBucket, euro, fmtDate } from '@fleet/shared';
import type { TimelineEvent } from '../car-detail/detail';

/**
 * De tijdlijn van één persoon: wanneer hij een auto of pas kreeg en weer inleverde,
 * en wat hij er tussendoor mee heeft uitgegeven.
 *
 * Dit is de gedetailleerde variant die eerst op de autopagina zat, en daar hoorde
 * hij niet: tientallen tankbeurten onder één auto zeggen iets over wie erin reed,
 * niet over de auto. Naast de koppelmomenten gaan ze wél ergens over, want dan
 * lees je in één kolom dat iemand op 8 juni de pas kreeg en drie dagen later voor
 * het eerst tankte.
 *
 * Onderhoud staat er niet in: dat hangt aan de auto en niet aan een pas, dus het
 * hoort in de tijdlijn van de auto.
 */

const KLEUR = {
  koppeling: '#3E7D63',
  mobiliteit: '#E0A943',
} as const;

/** Hoe een kostensoort in de tijdlijn heet. */
const SOORT: Record<string, string> = {
  brandstof: 'Getankt',
  laden: 'Geladen',
  parkeren: 'Geparkeerd',
  wassen: 'Gewassen',
  overige: 'Tankpas',
};

const nf = (n: number) => n.toLocaleString('nl-NL', { maximumFractionDigits: 2 });

/** Bedrag, hoeveelheid, prijs per eenheid en waar het was, op één regel. */
function transactieRegel(r: CostLine): string {
  const delen = [euro(r.bedragExcl)];
  if (r.hoeveelheid && r.eenheid) delen.push(`${nf(r.hoeveelheid)} ${r.eenheid}`);
  if (r.prijs && r.eenheid) delen.push(`${euro(r.prijs)}/${r.eenheid.toLowerCase()}`);
  // Het kenteken erbij: op een persoonspagina is dat de vraag die de autopagina
  // niet hoefde te beantwoorden, zeker bij iemand die van auto wisselde.
  if (r.kenteken && r.kenteken !== 'FICTIEF') delen.push(r.kenteken);
  const waar = r.station || r.plaats;
  if (waar) delen.push(waar);
  return delen.join(' · ');
}

/** Alle maanden waarin iets gebeurde, oudste eerst; voor de maandkiezer. */
export function tijdlijnMaanden(
  koppelMomenten: readonly KoppelTijdlijnItem[],
  costLines: readonly CostLine[],
): string[] {
  const maanden = new Set<string>();
  for (const k of koppelMomenten) if (k.datum) maanden.add(k.datum.slice(0, 7));
  for (const r of costLines) if (r.datum) maanden.add(r.datum.slice(0, 7));
  return [...maanden].sort();
}

/**
 * Hoort deze datum bij de gekozen periode?
 *
 * Een gebeurtenis zonder datum blijft alleen bij "alles" staan. Hem in een maand
 * meenemen zou beweren dat hij daarin viel, en dat weten we juist niet.
 */
function inPeriode(iso: string | null, periode: string, jaar: string | null): boolean {
  if (periode === 'ALL') return true;
  if (!iso) return false;
  if (periode === 'YTD') return jaar == null || iso.startsWith(jaar);
  return iso.startsWith(periode);
}

export function persoonTijdlijn(
  koppelMomenten: readonly KoppelTijdlijnItem[],
  costLines: readonly CostLine[],
  periode = 'ALL',
): TimelineEvent[] {
  const maanden = tijdlijnMaanden(koppelMomenten, costLines);
  const jaar = maanden.length ? maanden[maanden.length - 1]!.slice(0, 4) : null;
  const ruw: { iso: string | null; label: string; detail: string; color: string }[] = [];

  for (const k of koppelMomenten) {
    if (!inPeriode(k.datum, periode, jaar)) continue;
    ruw.push({ iso: k.datum, label: k.label, detail: k.detail, color: KLEUR.koppeling });
  }

  for (const r of costLines) {
    if (!inPeriode(r.datum, periode, jaar)) continue;
    const bucket = catToBucket(r.categorie);
    // Onderhoud kan hier niet voorkomen (geen pas), maar mocht het er ooit langs
    // komen, dan hoort het niet in deze tijdlijn.
    if (bucket === 'onderhoud') continue;
    ruw.push({
      iso: r.datum,
      label: `${SOORT[bucket] ?? 'Tankpas'}${r.product ? ` — ${r.product}` : ''}`,
      detail: transactieRegel(r),
      color: KLEUR.mobiliteit,
    });
  }

  return ruw
    .map((e) => ({ ...e, ts: e.iso ? new Date(`${e.iso}T00:00:00`).getTime() : Infinity }))
    .sort((a, b) => b.ts - a.ts)
    .map((e) => ({
      date: e.iso ? fmtDate(e.iso) : 'Onbekend',
      label: e.label,
      detail: e.detail,
      color: e.color,
    }));
}
